import Link from "next/link";
import { cn } from "@/lib/cn";

type Variant = "primary" | "secondary" | "outline" | "ghost" | "link";
type Size = "sm" | "md" | "lg";

type CommonProps = {
  variant?: Variant;
  size?: Size;
  className?: string;
  children: React.ReactNode;
  fullWidth?: boolean;
};

type AsButton = CommonProps &
  React.ButtonHTMLAttributes<HTMLButtonElement> & { href?: undefined };

type AsLink = CommonProps &
  Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "href"> & { href: string };

const base =
  "inline-flex items-center justify-center gap-2 font-medium transition-colors " +
  "disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none " +
  "focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[color:var(--color-primary)] " +
  "focus-visible:ring-offset-[color:var(--color-bg)]";

const variants: Record<Variant, string> = {
  primary:
    "bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-dark)]",
  secondary:
    "bg-[color:var(--color-secondary)] text-white hover:bg-[color:var(--color-secondary-muted)]",
  outline:
    "border border-[color:var(--color-border-strong)] text-[color:var(--color-text)] hover:bg-[color:var(--color-surface-alt)]",
  ghost:
    "text-[color:var(--color-text)] hover:bg-[color:var(--color-surface-alt)]",
  link: "text-[color:var(--color-primary)] hover:text-[color:var(--color-primary-dark)] underline underline-offset-4",
};

const sizes: Record<Size, string> = {
  sm: "h-9 px-3 text-sm rounded-[var(--radius-sm)]",
  md: "h-11 px-5 text-sm rounded-[var(--radius-md)]",
  lg: "h-13 px-7 text-base rounded-[var(--radius-md)]",
};

export default function Button(props: AsButton | AsLink) {
  const {
    variant = "primary",
    size = "md",
    fullWidth,
    className,
    children,
    ...rest
  } = props;
  const classes = cn(base, variants[variant], sizes[size], fullWidth && "w-full", className);

  if ("href" in rest && rest.href) {
    const { href, ...anchor } = rest;
    return (
      <Link href={href} className={classes} {...anchor}>
        {children}
      </Link>
    );
  }
  const btnProps = rest as AsButton;
  return (
    <button className={classes} {...btnProps}>
      {children}
    </button>
  );
}
